/**
 * Turning a DeviceOrientationEvent into a true-north compass heading.
 *
 * The one place that decides whether a reading is trustworthy, so the walking
 * radar in useExploreMode and the walk-to-pin overlay in useCompass cannot
 * drift apart on it. Returns null when the platform has not given us a
 * north-referenced reading, which callers must treat as "no compass" rather
 * than as zero.
 */

/** iOS Safari exposes the only true-north field on the event as a prefix. */
type OrientationEventish = Pick<DeviceOrientationEvent, "alpha" | "absolute"> & {
  webkitCompassHeading?: number;
};

/**
 * Degrees clockwise from true north, or null if the event cannot supply one.
 *
 * Two sources, in order of trust:
 *   - `webkitCompassHeading` (iOS Safari) is already degrees clockwise from
 *     north.
 *   - `alpha` is degrees counter-clockwise from north, but *only* on an event
 *     flagged `absolute`. On a plain deviceorientation event alpha is measured
 *     from wherever the device happened to be pointing when the listener
 *     attached. That is the trap this function exists to close: reading it
 *     anyway produced a dial that looked authoritative and was off by an
 *     arbitrary amount, which is worse than showing no dial at all.
 */
export function headingFromOrientation(e: OrientationEventish): number | null {
  const wk = e.webkitCompassHeading;
  if (typeof wk === "number" && Number.isFinite(wk)) return ((wk % 360) + 360) % 360;
  if (e.absolute === true && typeof e.alpha === "number" && Number.isFinite(e.alpha)) {
    return ((360 - e.alpha) % 360 + 360) % 360;
  }
  return null;
}
